import { useState, useEffect } from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';
import { db } from '@/lib/firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { useLanguage } from '@/contexts/LanguageContext';

type BilingualField = string | { ar: string; en: string };

interface ContactInfo {
  phone: string;
  email: string;
  address: BilingualField;
  mapUrl: string;
}

export function LandingContact() {
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();

  const toBilingual = (val: any): { ar: string; en: string } => {
    if (!val) return { ar: '', en: '' };
    if (typeof val === 'string') return { ar: val, en: '' };
    return { ar: val.ar || '', en: val.en || '' };
  };

  const localize = (field: BilingualField): string => {
    if (typeof field === 'string') return field;
    return field[language] || field.ar || field.en || '';
  };

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async () => {
    try {
      const docRef = doc(db, 'landing_contact', 'info');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setContactInfo({
          phone: data.phone || '',
          email: data.email || '',
          address: toBilingual(data.address),
          mapUrl: data.mapUrl || '',
        });
      }
    } catch (err) {
      console.error('Error fetching contact info:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">{t('landing.common.loading')}</div>
        </div>
      </section>
    );
  }

  if (!contactInfo) {
    return null;
  }

  return (
    <section id="contact" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-center text-[#004250] mb-12">{t('landing.contact.title')}</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-gray-50 p-8 rounded-lg shadow-lg">
            <div className="space-y-6">
              <div className="flex items-center space-x-4 space-x-reverse">
                <Phone className="w-6 h-6 text-[#7dda55]" />
                <div>
                  <h3 className="font-semibold text-[#004250]">{t('landing.contact.callUs')}</h3>
                  <p className="text-gray-600">{contactInfo.phone}</p>
                </div>
              </div>
              <div className="flex items-center space-x-4 space-x-reverse">
                <Mail className="w-6 h-6 text-[#7dda55]" />
                <div>
                  <h3 className="font-semibold text-[#004250]">{t('landing.contact.email')}</h3>
                  <p className="text-gray-600">{contactInfo.email}</p>
                </div>
              </div>
              <div className="flex items-center space-x-4 space-x-reverse">
                <MapPin className="w-6 h-6 text-[#7dda55]" />
                <div>
                  <h3 className="font-semibold text-[#004250]">{t('landing.contact.address')}</h3>
                  <p className="text-gray-600">{localize(contactInfo.address)}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="h-[400px] rounded-lg overflow-hidden shadow-lg">
            <iframe
              src={contactInfo.mapUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </div>
    </section>
  );
}
